import React, { useRef, useEffect, useState } from "react";
import { useRouter } from "next/router";

import Box from "@commerce-ui/core2/Box";
import useWindowSize from "@commerce-ui/core2/useWindowSize";

import "../../globals";
import { default as themeDajar } from "../../theme";
import disabledFilters from "../../../conf/disabledFilters";

import translate from "@deosite/helpers/langLibrary";
import categoryInitialProps from "@deosite/helpers/categoryInitialProps";
import THEMES from "@deosite/helpers/categoryThemes";
import {
  getSubcategories,
  getSubcategoriesForLeftColumn,
  getParentCategory
} from "@deosite/helpers/getSubcategories";

import MetaTags from "../../components/MetaTags";
import CategoriesSliderMobile from "../../components/CategoriesSliderMobile";
import Pagination from "../../components/Pagination";
import FiltersBarDesktop from "../../components/FiltersBarDesktop";
import FiltersBarMobile from "../../components/FiltersBarMobile";

import BrandPage from "./brand";
import BrandsListPage from "./brands-list";
import CollectionsListPage from "./collections-list";
import CollectionPage from "./collection";
import InspirationPage from "./inspiration";
import Category from "./category";
import InspirationsListPage from "./inspirations-list";
import getRoute from "../../getRoute";

import useCommitedFilters from "@deosite/api/hooks/common/useNormalizeAndMapFilters";
import useFilters from "@deosite/api/hooks/REST/useFilters";
import { usePaginationLoader } from "@deosite/api/hooks/common/usePaginationLoader";
import { useLoader } from "@deosite/api/hooks/common/useLoader";

import {
  useProductsAndFiltersFromCategory,
  fetcher as productsAndFiltersFromCategoryFetcher
} from "@deosite/api/hooks/REST/useProductsAndFiltersFromCategory";

import {
  useCategory,
  fetcher as categoryFetcher
} from "@deosite/api/hooks/REST/useCategory";

import {
  fetcher as prismicFetcher,
  usePrismic
} from "@deosite/api/hooks/prismic/usePrismic";

const CategoryPage = props => {
  const {
    initialCurrentCategory,
    categories,
    initialPrismicCategory,
    language,
    categoryBanners,
    categoryId,
    requestPath,
    pageSize,
    theme,
    cache
  } = props;

  let { pickedFilters, initialProductsAndFilters } = props;

  const router = useRouter();

  const initialPage = router.asPath.match(/p=([0-9]*)/)?.[1] ?? 1;

  const { isLoading, setPaginationLoadingState } = usePaginationLoader();

  const firstUpdate = useRef(true);
  const size = useWindowSize();

  const isMobile = size.width <= parseInt(themeDajar().breakpoints.md);

  const { externalContentTimeout } = useLoader();

  const [filtersReady, setFiltersReady] = useState(false);

  useEffect(() => {
    if (filtersReady) {
      return;
    }
    if (isMobile !== null) {
      setFiltersReady(true);
    }
  }, [isMobile]);

  const {
    data: { products = [], productsTotalCount, filters, narrowedFilters }
  } = useProductsAndFiltersFromCategory(
    categoryId,
    pickedFilters,
    initialPage,
    pageSize,
    null,
    language,
    initialProductsAndFilters,
    router,
    cache
  );

  const {
    products: categoryProducts,
    page,
    activeFilters,
    onFiltersChange
  } = useFilters(
    products,
    categoryId,
    narrowedFilters,
    initialPage,
    filters,
    router,
    pageSize,
    firstUpdate.current === true,
    cache,
    disabledFilters
  );

  const { filters: filtersData } = useCommitedFilters({
    data: activeFilters
  });

  let { data: currentCategory = {} } = useCategory(
    categoryId,
    initialCurrentCategory
  );

  currentCategory.name = currentCategory?.name || "";

  const { data: prismicCategory } = usePrismic(
    "category",
    "category-" + categoryId,
    language,
    initialPrismicCategory
  );

  const translations = {
    seoDescription: translate("Meta description", {
      categoryName: currentCategory.name
    }),
    seoTitle: `${currentCategory.name} | Dajar `,
    home: translate("Home")
  };

  const scrollToRef = useRef();
  const onPageChange = async pageToSet => {
    if (isLoading) return;

    setPaginationLoadingState(true);
    const asPath = router.asPath;
    const urlPath =
      asPath.indexOf("?p=") >= 0 ? asPath.split("?p=") : asPath.split("&p=");
    const pathAlreadyContainsQuestionMark = urlPath[0]?.indexOf("?") >= 0;

    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const currentPosition = scrollToRef.current?.getBoundingClientRect();

    currentPosition
      ? window.scrollTo(0, currentPosition.top + scrollTop - 150)
      : window.scrollTo(0, 0);

    const pageStringToSet = pathAlreadyContainsQuestionMark
      ? `&p=${pageToSet}`
      : `?p=${pageToSet}`;

    await onFiltersChange(
      activeFilters,
      pageToSet,
      requestPath,
      categories,
      currentCategory,
      prismicCategory
    );

    router
      .push(`/[...categoryOrProductPage]`, `${urlPath[0]}${pageStringToSet}`, {
        shallow: true
      })
      .then(() => window.scrollTo(0, currentPosition))
      .then(() => setPaginationLoadingState(false));
  };

  const pagesAmount = Math.ceil(productsTotalCount / pageSize);

  const categorySubcategories = getSubcategories(
    categories,
    currentCategory,
    getRoute,
    router
  );

  const subcategoryForLeftColumn = getSubcategoriesForLeftColumn(
    categories,
    currentCategory
  );

  const parentCategory = getParentCategory(categories, currentCategory);

  const backLabel =
    currentCategory.level > 3 ? parentCategory?.name : translations.home;

  const backClickFunction = () => {
    currentCategory.level > 3
      ? router.push(
          getRoute("category-page", parentCategory).href,
          "/" + parentCategory.url
        )
      : router.push(getRoute("home-page").href, "/");
  };

  const changeUrlAfterFiltering = async filters => {
    const filtersString = await onFiltersChange(
      filters,
      1,
      requestPath,
      categories,
      currentCategory,
      prismicCategory
    );
    router.push(
      `/[...categoryOrProductPage]`,
      `/${requestPath}${filtersString}`,
      {
        shallow: true
      }
    );
  };
  const isDialogMobileOpen = useRef(false);
  const filtersBar = filtersReady && (
    <Box ref={scrollToRef}>
      {isMobile || isDialogMobileOpen.current ? (
        <FiltersBarMobile
          data={activeFilters}
          onChange={changeUrlAfterFiltering}
          isDialogMobileOpen={isDialogMobileOpen}
        />
      ) : (
        <FiltersBarDesktop
          data={activeFilters}
          onChange={changeUrlAfterFiltering}
        />
      )}
    </Box>
  );

  const categoriesSliderMobile = isMobile !== null && isMobile && (
    <CategoriesSliderMobile categories={subcategoryForLeftColumn} />
  );

  const pagination = (
    <Pagination
      pagesAmount={pagesAmount}
      currentPage={page ?? 1}
      onChange={onPageChange}
    />
  );

  const categoryBanner =
    categoryBanners &&
    !externalContentTimeout &&
    categoryBanners.find(banner => {
      if (banner.categoryId === parseInt(categoryId)) {
        return banner;
      }
    });

  let chosenFiltersValues = "";
  let collectionFromFilters;
  let noFollow = false;
  let collectionFromFiltersString = "";
  if (activeFilters) {
    try {
      chosenFiltersValues = activeFilters
        .filter(
          filter => !["range", "select"].includes(filter.type) && filter.value
        )
        .map(
          filter =>
            Array.isArray(filter.value) && filter.value.map(value => value.name)
        )
        .flat();

      collectionFromFilters = activeFilters
        .filter(
          filter =>
            !["range", "select"].includes(filter.type) &&
            filter.value &&
            filter.id === "dajar_collection"
        )
        .map(filter => filter.value.map(value => value.name))
        .flat();

      collectionFromFilters?.length > 0 &&
        collectionFromFilters.map(
          filter => (collectionFromFiltersString += filter + ", ")
        );
      collectionFromFiltersString = collectionFromFiltersString.substring(
        0,
        collectionFromFiltersString.length - 2
      );

      noFollow =
        activeFilters
          .filter(filter => filter.value)
          .map(filter =>
            Array.isArray(filter.value)
              ? filter.value.map(value => value.name)
              : filter.value.id
          )
          .flat().length > 2 ||
        activeFilters.find(
          filter => Array.isArray(filter.value) && filter.value.length > 1
        ) ||
        (activeFilters.length === 1 && activeFilters[0].id === "sort") ||
        activeFilters.find(filter => filter.id === "sort")?.value ||
        activeFilters.find(filter => filter.type === "range" && filter.value);
    } catch (e) {
      console.error(
        "Filters category",
        activeFilters,
        e,
        initialCurrentCategory,
        categoryId,
        language
      );
    }
  }
  const renderWithMetaTags = Component => {
    return (
      <>
        <MetaTags
          page={MetaTags.CATEGORY_PAGE}
          description={
            currentCategory.meta?.description
              ? currentCategory.meta?.description
              : translations.seoDescription
          }
          title={
            currentCategory.meta?.title
              ? currentCategory.meta.title
              : translations.seoTitle
          }
          currentPageNumber={page > 1 ? page : 0}
          imageUrl={categoryProducts[0]?.image.originalSrc}
          tags={chosenFiltersValues}
          url={currentCategory.url}
          noFollow={noFollow}
        />
        {Component}
      </>
    );
  };

  switch (theme) {
    case THEMES.CATEGORY:
      return renderWithMetaTags(
        <Category
          key={categoryId}
          currentCategory={currentCategory}
          parentCategory={{
            name: parentCategory?.name,
            url: parentCategory?.url
          }}
          categoryProducts={categoryProducts}
          filtersBar={filtersBar}
          backLabel={backLabel}
          backClickFunction={backClickFunction}
          footerContent={props.footerContent}
          pagination={pagination}
          dataRequiredToHideLoader={[categories, prismicCategory]}
          prismicData={prismicCategory}
          categoryBanner={categoryBanner}
          filtersString={
            chosenFiltersValues.length
              ? chosenFiltersValues.length > 3
                ? chosenFiltersValues
                    .slice()
                    .splice(0, 3)
                    .join(", ") + "... - "
                : chosenFiltersValues.join(", ") + " - "
              : ""
          }
          totalCount={productsTotalCount}
        />
      );
    case THEMES.MAIN:
      return renderWithMetaTags(
        <Category
          key={categoryId}
          subcategoryForLeftColumn={subcategoryForLeftColumn}
          currentCategory={currentCategory}
          parentCategory={{
            name: parentCategory?.name,
            url: parentCategory?.url
          }}
          categoryProducts={categoryProducts}
          filtersBar={filtersBar}
          backLabel={backLabel}
          backClickFunction={backClickFunction}
          footerContent={props.footerContent}
          pagination={pagination}
          categoriesSliderMobile={categoriesSliderMobile}
          dataRequiredToHideLoader={[categories, prismicCategory]}
          prismicData={prismicCategory}
          isDefaultView={false}
          categoryBanner={categoryBanner}
          filtersString={
            chosenFiltersValues.length
              ? chosenFiltersValues.length > 3
                ? chosenFiltersValues
                    .slice()
                    .splice(0, 3)
                    .join(", ") + "... - "
                : chosenFiltersValues.join(", ") + " - "
              : ""
          }
          totalCount={productsTotalCount}
        />
      );
    case THEMES.COLLECTION:
      return renderWithMetaTags(
        <CollectionPage
          key={categoryId}
          prismicData={prismicCategory}
          currentCategory={currentCategory}
          parentCategory={{
            name: parentCategory?.name,
            url: parentCategory?.url
          }}
          categoryProducts={categoryProducts && categoryProducts}
          backLabel={backLabel}
          backClickFunction={backClickFunction}
          footerContent={props.footerContent}
          dataRequiredToHideLoader={[prismicCategory, categoryProducts]}
          pagination={pagination}
          addToCartDisabled={
            language.language === global.__appConfig__.shopOffGB
          }
          totalCount={productsTotalCount}
        />
      );

    case THEMES.COLLECTION_LIST:
      return renderWithMetaTags(
        <CollectionsListPage
          key={categoryId}
          collectionCategoryName={currentCategory && currentCategory.name}
          onFiltersChange={onFiltersChange}
          filters={filters}
          backLabel={backLabel}
          backClickFunction={backClickFunction}
          footerContent={props.footerContent}
          collections={prismicCategory?.content?.collections}
        />
      );

    case THEMES.BRAND:
      return renderWithMetaTags(
        <BrandPage
          key={categoryId}
          allCollectionProducts={categoryProducts && categoryProducts}
          brandName={currentCategory && currentCategory.name}
          backLabel={backLabel}
          backClickFunction={backClickFunction}
          footerContent={props.footerContent}
          pagination={pagination}
          filtersBar={filtersBar}
          dataRequiredToHideLoader={[categoryProducts]}
          prismicData={prismicCategory}
          collectionFromFiltersString={collectionFromFiltersString}
          totalCount={productsTotalCount}
        />
      );

    case THEMES.BRAND_LIST:
      return renderWithMetaTags(
        <BrandsListPage
          key={categoryId}
          title={currentCategory && currentCategory.name}
          menuItems={categorySubcategories && categorySubcategories}
          backLabel={backLabel}
          backClickFunction={backClickFunction}
          footerContent={props.footerContent}
        />
      );

    case THEMES.INSPIRATION_LIST:
      return renderWithMetaTags(
        <InspirationsListPage
          key={categoryId}
          categoryName={currentCategory && currentCategory.name}
          backLabel={backLabel}
          backClickFunction={backClickFunction}
          inspirations={prismicCategory?.content?.inspirations}
          subcategories={subcategoryForLeftColumn}
          footerContent={props.footerContent}
        />
      );

    case THEMES.INSPIRATION:
      return renderWithMetaTags(
        <InspirationPage
          key={categoryId}
          prismicData={prismicCategory}
          categoryProducts={categoryProducts && categoryProducts}
          backLabel={backLabel}
          backClickFunction={backClickFunction}
          footerContent={props.footerContent}
          dataRequiredToHideLoader={[categoryProducts]}
          addToCartDisabled={
            language.language === global.__appConfig__.shopOffGB
          }
        />
      );

    default:
      return renderWithMetaTags(
        <Category
          key={categoryId}
          currentCategory={currentCategory}
          parentCategory={{
            name: parentCategory?.name,
            url: parentCategory?.url
          }}
          categoryProducts={categoryProducts}
          filtersBar={filtersBar}
          backLabel={backLabel}
          backClickFunction={backClickFunction}
          footerContent={props.footerContent}
          pagination={pagination}
          dataRequiredToHideLoader={[categories, prismicCategory]}
          prismicData={prismicCategory}
          categoryBanner={categoryBanner}
          totalCount={productsTotalCount}
        />
      );
  }
};

CategoryPage.getInitialProps = async context =>
  await categoryInitialProps({
    context,
    productsAndFiltersFromCategoryFetcher,
    categoryFetcher,
    prismicFetcher
  });

export default CategoryPage;
